import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import css from './trialLesson.module.css';
import Header from '../../components/standartComponents/header';
import LessonSteps from '../../components/lesson/LessonSteps';

const TrialLesson = ({ authUser }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [videoCompleted, setVideoCompleted] = useState(false);
  const [playInterval, setPlayInterval] = useState(null);
  
  const totalSteps = 4;

  const togglePlay = () => {
    if (videoCompleted) return;
    if (isPlaying) {
      pauseVideo();
    } else {
      playVideo();
    }
  };

  const playVideo = () => {
    setIsPlaying(true);
    const interval = setInterval(() => {
      setProgress(p => {
        if (p >= 100) {
          clearInterval(interval);
          completeVideo();
          return 100;
        }
        return p + 12.5; // Simulate 8 seconds
      });
    }, 1000);
    setPlayInterval(interval);
  };

  const pauseVideo = () => {
    setIsPlaying(false);
    if (playInterval) clearInterval(playInterval);
  };

  const completeVideo = () => {
    setIsPlaying(false);
    setVideoCompleted(true);
  };

  const replayVideo = () => {
    setVideoCompleted(false);
    setProgress(0);
    playVideo();
  };

  const handleNextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    } else {
      setIsCompleted(true);
    }
  };

  const pct = Math.round((currentStep / totalSteps) * 100);

  const getCurTime = () => {
    const sec = Math.round((progress / 100) * 8);
    return `0:0${sec}`;
  };

  return (
    <div className={css.trialPage}>
      <div className={css.gridBg}></div>
      
      {/* HEADER */}
      <Header authUser={authUser} />

      <main className={css.main}>
        {/* BACK */}
        <div className={css.back}>
          <Link to="/">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg>
            Повернутися на головну
          </Link>
        </div>

        <div className={css.page}>
          {/* TOP BAR */}
          <div className={css.topbar}>
            <div className={css.tbLeft}>
              <div className={css.tbBadge}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                Пробний урок
              </div>
              <div>
                <div className={css.tbTitle}>Пробний 2-хвилинний урок</div>
                <div className={css.tbDesc}>Перегляньте відео, а потім натисніть «Далі: слова» щоб продовжити.</div>
              </div>
            </div>
            <div className={css.tbRight}>
              <div className={css.tbProg}>
                <div className={css.tbProgTop}>Крок <span id="stepNum">{currentStep}</span> з 4 · <span className={css.tbProgPct}>{pct}%</span></div>
                <div className={css.tbProgBar}><div className={css.tbProgFill} style={{ width: `${pct}%` }}></div></div>
              </div>
              <button className={css.btnNext} onClick={handleNextStep}>
                Далі: слова 
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              </button>
            </div>
          </div>

          {/* REGISTRATION PROMPT */}
          {!authUser && (
            <div className={css.regBar}>
              <div className={css.regLeft}>
                <div className={css.regIcon}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></svg>
                </div>
                <div>
                  <div className={css.regTitle}>Ваш прогрес не збережеться</div>
                  <div className={css.regDesc}>Зареєструйтесь, щоб не втратити результат уроку</div>
                </div>
              </div>
              <button className={css.regBtn} onClick={() => navigate('/signup')}>
                Зберегти прогрес 
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              </button>
            </div>
          )}

          {/* VIDEO CARD */}
          <div>
            <div className={css.videoCard}>
              <div className={css.vp} onClick={togglePlay}>
                <div className={`${css.vpOrb} ${css.vpO1}`}></div>
                <div className={`${css.vpOrb} ${css.vpO2}`}></div>
                <div className={`${css.vpBubble} ${css.vpB1}`}>Hello! 👋</div>
                <div className={`${css.vpBubble} ${css.vpB2}`}>How are you?</div>
                <div className={css.vpAvatars}>
                  <div className={`${css.vpAv} ${css.vpAv1}`}>👩</div>
                  <div className={`${css.vpAv} ${css.vpAv2}`}>👨</div>
                </div>
                <div className={`${css.vpPlay} ${isPlaying ? css.playing : ''}`}>
                  {isPlaying ? (
                    <svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                  )}
                </div>
                <div className={css.vpControls} onClick={e => e.stopPropagation()}>
                  <div className={css.vpTime}>{getCurTime()} / 0:08</div>
                  <div className={css.vpBar}><div className={css.vpBarFill} style={{ width: `${progress}%` }}></div></div>
                  <div className={css.vpVol}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg></div>
                  <div className={css.vpFs}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M21 8V5a2 2 0 0 0-2-2h-3"/><path d="M3 16v3a2 2 0 0 0 2 2h3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/></svg></div>
                </div>
                {videoCompleted && (
                  <div className={css.vpDone} style={{ display: 'flex' }} onClick={e => e.stopPropagation()}>
                    <div className={css.vpDoneCheck}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg></div>
                    <div className={css.vpDoneText}>Відео переглянуто ✓</div>
                    <div className={css.vpDoneReplay} onClick={replayVideo}>Переглянути ще раз</div>
                  </div>
                )}
              </div>
              <div className={css.vInfo}>
                <div className={css.vInfoLeft}>
                  <div className={css.vLevel}><div className={css.vLevelDot}></div>Pre-Beginner</div>
                  <div className={css.vTitle}>Super Simple Dialogue — Hello, How are You</div>
                  <div className={css.vTopic}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>Socializing & Small Talk</div>
                </div>
                <div className={css.vInfoRight}>
                  <div className={css.vNote}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>Після перегляду переходьте до слів</div>
                  <button className={css.btnNext} onClick={handleNextStep}>Далі: слова <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg></button>
                </div>
              </div>
            </div>
          </div>

          {/* SIDEBAR — Design 27 unified component */}
          <div className={css.sidebar}>
            <LessonSteps
              currentStep={currentStep}
              onStepClick={(step) => setCurrentStep(step)}
            />
          </div>
        </div>
      </main>

      {/* COMPLETION STATE */}
      <div className={`${css.completion} ${isCompleted ? css.visible : ''}`}>
        <div className={css.compInner}>
          <div className={css.compIconWrap}>
            <div className={css.compIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
            </div>
            <span className={`${css.compSparkle} ${css.sp1}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg></span>
            <span className={`${css.compSparkle} ${css.sp2}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg></span>
            <span className={`${css.compSparkle} ${css.sp3}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg></span>
          </div>

          <h2 className={css.compTitle}>Ви блискуче пройшли пробний урок!</h2>
          <p className={css.compSub}>Тепер оберіть, як хочете навчатися далі.</p>

          <div className={css.compCards}>
            {/* Primary: Personalized Plan */}
            <div className={`${css.compCard} ${css.ccPrimary}`} onClick={() => navigate('/roadMap')}>
              <span className={css.compBadge}>★ Рекомендовано</span>
              <div className={`${css.ccIcon} ${css.cciPlan}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
              </div>
              <h3>Отримати персоналізований план</h3>
              <p className={css.ccDesc}>Ми створимо курс під вашу ціль, рівень і темп навчання.</p>
              <div className={css.ccNote}>💡 Якщо ви не знаєте свій рівень, ми запропонуємо короткий тест під час створення плану.</div>
              <button className={`${css.compCta} ${css.compCtaPrimary}`}>
                Створити мій план
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              </button>
            </div>

            {/* Secondary: Learn Without a Plan */}
            <div className={css.compCard} onClick={() => navigate('/catalog')}>
              <div className={`${css.ccIcon} ${css.cciFree}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="7" height="7" x="3" y="3" rx="1"/><rect width="7" height="7" x="14" y="3" rx="1"/><rect width="7" height="7" x="14" y="14" rx="1"/><rect width="7" height="7" x="3" y="14" rx="1"/></svg>
              </div>
              <h3>Навчатися без плану</h3>
              <p className={css.ccDesc}>Обирайте будь-які відео та практикуйтеся у власному темпі.</p>
              <button className={`${css.compCta} ${css.compCtaSecondary}`}>
                Перейти до відео
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              </button>
            </div>
          </div>

          <p className={css.compFootnote}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
            Ви завжди зможете перейти між цими режимами пізніше.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TrialLesson;
